import { Component, OnDestroy, OnInit } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { CompositeKey, KeyValuePair, RunView } from '@memberjunction/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ResourceData } from '@memberjunction/core-entities';

/**
 * @fileoverview Hello Dashboard Component - A Comprehensive MemberJunction Dashboard Demo
 *
 * OVERVIEW:
 * =========
 * This component serves as a comprehensive demonstration of how to create a fully custom dashboard
 * within the MemberJunction Explorer application. While the screensaver-style animation is intended to be a
 * a bit playful, it showcases serious architectural patterns and integration techniques that are essential for
 * building production-quality business applications.
 *
 * BUSINESS VALUE:
 * ===============
 * This example demonstrates how MemberJunction enables developers to create completely custom
 * user interfaces that can seamlessly integrate with the broader application ecosystem while
 * maintaining full control over user experience, data interaction, and business logic.
 *
 * KEY ARCHITECTURAL PATTERNS DEMONSTRATED:
 * ========================================
 *
 * 1. CUSTOM UI DEVELOPMENT:
 *    - Complete control over HTML, CSS, and TypeScript
 *    - Modern responsive design with CSS animations
 *    - Hardware-accelerated graphics for smooth performance
 *    - Custom event handling and user interactions
 *
 * 2. MEMBERJUNCTION INTEGRATION:
 *    - Extends BaseDashboard for framework integration
 *    - Proper component registration with @RegisterClass
 *    - Lifecycle management (ngOnInit, ngOnDestroy, initDashboard, loadData, Refresh)
 *    - Event emission for container communication
 *
 * 3. DATA ACCESS & PERSISTENCE:
 *    - RunView usage for querying entity data
 *    - UserState persistence for maintaining user preferences
 *    - RxJS-based debouncing to optimize database writes
 *    - Proper error handling and fallback mechanisms
 *
 * 4. CONTAINER COMMUNICATION:
 *    - OpenEntityRecord.emit() for navigation requests
 *    - UserStateChanged.emit() for state persistence
 *    - Interaction.emit() for analytics and tracking
 *    - Proper event-driven architecture
 *
 * 5. ADVANCED UI FEATURES:
 *    - Smooth 60fps animations using requestAnimationFrame
 *    - Time-based movement calculations for consistent performance
 *    - Responsive design that adapts to container resizing
 *    - Keyboard shortcuts for enhanced user experience
 *    - Visual feedback and accessibility considerations
 *
 * 6. STATE MANAGEMENT:
 *    - Complex state object with multiple properties
 *    - Automatic state restoration on component load
 *    - Debounced updates to prevent database flooding
 *    - Proper cleanup and memory management
 *
 * REAL-WORLD APPLICATIONS:
 * ========================
 * The patterns demonstrated here can be applied to build:
 * - Real-time monitoring dashboards
 * - Interactive data visualization components
 * - Custom form builders and editors
 * - Specialized business workflow interfaces
 * - Gaming or simulation interfaces
 * - Custom reporting and analytics views
 *
 * SETUP INSTRUCTIONS:
 * ===================
 * To use this dashboard in your application:
 * 1. Create a record in the `Dashboard User Preferences` entity:
 *    INSERT INTO __mj.DashboardUserPreference (DashboardID, Scope, DisplayOrder)
 *    VALUES ('61C9433E-F36B-1410-8DAB-00021F8B792E','Global',0)
 * 2. The DashboardID above corresponds to this HelloDemo component
 * 3. Adjust DisplayOrder to control positioning in the dashboard list
 *
 * PERFORMANCE CONSIDERATIONS:
 * ===========================
 * - Uses requestAnimationFrame for optimal rendering performance
 * - Implements proper cleanup to prevent memory leaks
 * - Debounces database writes to reduce server load
 * - Uses CSS transforms for hardware-accelerated animations
 * - Minimizes DOM queries and caches element references
 *
 * EXTENSIBILITY:
 * ==============
 * This component can be extended or modified to:
 * - Add more complex animations and interactions
 * - Integrate with different data sources
 * - Implement custom business logic
 * - Add real-time data updates via WebSockets
 * - Create multi-user collaborative features
 *
 * @author MemberJunction Development Team
 * @since 2.0.x
 * @version 1.0.0
 * @category Dashboard Components
 * @example
 * // Register the component in your module
 * import { LoadHelloDashboard } from './path/to/hello-dashboard.component';
 * LoadHelloDashboard(); // Prevents tree-shaking
 */
@Component({
  standalone: false,
  selector: 'mj-hello-dashboard',
  templateUrl: './hello-dashboard.component.html',
  styleUrls: ['./hello-dashboard.component.scss']
})
@RegisterClass(BaseDashboard, 'HelloDemo')
export class HelloDashboardComponent extends BaseDashboard implements OnInit, OnDestroy {

  private readonly colors: string[] = [
    '#FF5733', '#33FF57', '#3357FF', '#F033FF', '#FF33A6', '#33FFF6',
    '#FFD133', '#9C33FF', '#FF8C33', '#FF6B6B', '#4ECDC4', '#45B7D1',
    '#96CEB4', '#FFEAA7', '#DDA0DD', '#FFB6C1', '#20B2AA', '#87CEEB'
  ];

  protected textColor: string = this.colors[0];
  protected themeColor: string = this.colors[0];

  private animationFrame: number = 0;
  private entityChangeInterval: ReturnType<typeof setInterval> | null = null;
  private isAnimating: boolean = false;
  private lastTime: number = 0;

  protected boxPosition = { x: 50, y: 50 };
  private velocity = { x: 80, y: 60 };
  protected speed: number = 0.5;
  private containerSize = { width: 800, height: 600 };
  private boxSize = { width: 400, height: 300 };

  protected entities: any[] = [];
  protected featuredEntity: string = 'Loading entities...';
  protected featuredEntityObject: any = null;

  protected override destroy$ = new Subject<void>();
  private userStateSubject = new BehaviorSubject<any>(null);
  protected showStateUpdateNotification: boolean = false;
  protected isLoading: boolean = true;

  private get currentUserState() {
    return {
      lastColor: this.textColor,
      themeColor: this.themeColor,
      speed: this.speed,
      featuredEntity: this.featuredEntity
    };
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.loadUserState();
    this.setupUserStateDebouncing();
    this.setupResizeListener();
    this.setupKeyboardListeners();
  }

  override ngOnDestroy(): void {
    const currentState = this.currentUserState;
    if (currentState) {
      console.log('💾 HelloDashboard: Saving final user state on destroy:', currentState);
      this.UserStateChanged.emit(currentState);
    }

    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAnimation();
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private loadUserState(): void {
    console.log('🔄 HelloDashboard: Loading user state during initialization');
    console.log('📋 HelloDashboard: Full Config object:', this.Config);

    const state = this.Config?.userState;
    console.log('💾 HelloDashboard: Retrieved user state:', state);

    if (state) {
      console.log('✅ HelloDashboard: Restoring state from saved data (only allowed values)');

      if (state.lastColor) {
        console.log(`🎨 HelloDashboard: Restoring text color: ${state.lastColor}`);
        this.textColor = state.lastColor;
      }

      if (state.themeColor) {
        console.log(`🎨 HelloDashboard: Restoring theme color: ${state.themeColor}`);
        this.themeColor = state.themeColor;
      } else if (state.lastColor) {
        console.log(`🎨 HelloDashboard: Using text color as theme fallback: ${state.lastColor}`);
        this.themeColor = state.lastColor;
      }

      if (state.speed !== undefined) {
        console.log(`⚡ HelloDashboard: Restoring speed: ${state.speed}`);
        this.speed = state.speed;
      }

      if (state.featuredEntity) {
        console.log(`🏢 HelloDashboard: Restoring featured entity: ${state.featuredEntity}`);
        this.featuredEntity = state.featuredEntity;
      }

      const cleanState = {
        lastColor: this.textColor,
        themeColor: this.themeColor,
        speed: this.speed,
        featuredEntity: this.featuredEntity
      };

      if (Object.keys(state).length > 4 ||
          state.hasOwnProperty('boxPosition') ||
          state.hasOwnProperty('velocity') ||
          state.hasOwnProperty('curveMotion')) {
        console.log('🧪 HelloDashboard: Cleaning up legacy user state values');
        this.UserStateChanged.emit(cleanState);
      }
    } else {
      console.log('🆕 HelloDashboard: No saved state found, starting with slow speed and random colors');
      this.speed = 0.5;
      this.setRandomColor();
    }

    console.log('🎯 HelloDashboard: Final component state after loading:', {
      textColor: this.textColor,
      themeColor: this.themeColor,
      speed: this.speed,
      featuredEntity: this.featuredEntity
    });

    this.isLoading = false;
    this.NotifyLoadComplete();

    setTimeout(() => {
      console.log('🔄 HelloDashboard: Delayed container size update');
      this.updateContainerSize();
    }, 1000);
  }

  async GetResourceDisplayName(data: ResourceData): Promise<string> {
    return "Hello World Dashboard"
  }

  private setupUserStateDebouncing(): void {
    this.userStateSubject.pipe(
      debounceTime(3000),
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state) {
        console.log('🔄 HelloDashboard: Persisting user state to container:', state);

        this.UserStateChanged.emit(state);

        this.showStateUpdateNotification = true;

        setTimeout(() => {
          this.showStateUpdateNotification = false;
        }, 3000);

        setTimeout(() => {
          console.log('🔍 HelloDashboard: Current Config after state update:', this.Config);
        }, 1000);
      }
    });
  }

  private setupResizeListener(): void {
    window.addEventListener('resize', this.onWindowResize);
  }

  private onWindowResize = (): void => {
    if (this.isAnimating) {
      setTimeout(() => {
        this.updateContainerSize();
      }, 100);
    }
  }

  private setupKeyboardListeners(): void {
    window.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA') {
      return;
    }

    switch (event.key) {
      case '+':
      case '=':
        event.preventDefault();
        this.SpeedUp();
        break;
      case '-':
      case '_':
        event.preventDefault();
        this.SlowDown();
        break;
    }
  }

  private updateUserState(): void {
    const state = this.currentUserState;
    console.log('⏰ HelloDashboard: Queuing state update for debouncing:', state);
    this.userStateSubject.next(state);
  }

  protected override initDashboard(): void {
    if (!this.isLoading) {
      this.startAnimation();
    }
  }

  protected override async loadData(): Promise<void> {
    try {
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: 'MJ: Entities',
        OrderBy: 'Name',
        ExtraFilter: '',
      });

      if (result && result.Success && result.Results) {
        this.entities = result.Results;
        if (this.entities.length > 0) {
          this.startEntityRotation();
        }
      }
    } catch (error) {
      console.error('Error loading entities:', error);
      this.entities = [
        { Name: 'Demo Entity 1', ID: '1' },
        { Name: 'Demo Entity 2', ID: '2' },
        { Name: 'Demo Entity 3', ID: '3' }
      ];
      this.startEntityRotation();
    }
  }

  private startAnimation(): void {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.lastTime = performance.now();
    this.updateContainerSize();

    const maxWidth = this.containerSize.width - this.boxSize.width;
    const maxHeight = this.containerSize.height - this.boxSize.height;

    if (maxWidth > 0 && maxHeight > 0) {
      this.boxPosition.x = Math.random() * maxWidth;
      this.boxPosition.y = Math.random() * maxHeight;
    } else {
      this.boxPosition.x = 50;
      this.boxPosition.y = 50;
    }

    this.velocity.x = 80;
    this.velocity.y = 60;

    console.log('🎥 HelloDashboard: Starting animation with velocity:', this.velocity);
    console.log('📍 HelloDashboard: Starting position:', this.boxPosition);
    console.log('📏 HelloDashboard: Container size:', this.containerSize);
    console.log('📦 HelloDashboard: Box size:', this.boxSize);

    this.animate();
  }

  private stopAnimation(): void {
    this.isAnimating = false;

    if (this.entityChangeInterval) {
      clearInterval(this.entityChangeInterval);
    }

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  private animate = (currentTime: number = performance.now()): void => {
    if (!this.isAnimating) return;

    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    this.updateBoxPosition(deltaTime);

    this.animationFrame = requestAnimationFrame(this.animate);
  }

  private updateContainerSize(): void {
    const container = document.querySelector('.hello-dashboard-container') as HTMLElement;
    const box = document.querySelector('.greeting-box') as HTMLElement;

    if (container && box) {
      const containerRect = container.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();

      this.containerSize = {
        width: containerRect.width,
        height: containerRect.height
      };

      this.boxSize = {
        width: boxRect.width,
        height: boxRect.height
      };
    }
  }

  private updateBoxPosition(deltaTime: number): void {
    const moveX = this.velocity.x * this.speed * deltaTime;
    const moveY = this.velocity.y * this.speed * deltaTime;

    this.boxPosition.x += moveX;
    this.boxPosition.y += moveY;

    const minX = 0;
    const maxX = this.containerSize.width - this.boxSize.width;
    const minY = 0;
    const maxY = this.containerSize.height - this.boxSize.height;

    if (this.boxPosition.x <= minX) {
      this.boxPosition.x = minX;
      this.velocity.x = Math.abs(this.velocity.x);
    } else if (this.boxPosition.x >= maxX) {
      this.boxPosition.x = maxX;
      this.velocity.x = -Math.abs(this.velocity.x);
    }

    if (this.boxPosition.y <= minY) {
      this.boxPosition.y = minY;
      this.velocity.y = Math.abs(this.velocity.y);
    } else if (this.boxPosition.y >= maxY) {
      this.boxPosition.y = maxY;
      this.velocity.y = -Math.abs(this.velocity.y);
    }
  }

  private startEntityRotation(): void {
    if (this.entities.length === 0) return;

    this.changeFeaturedEntity();

    this.entityChangeInterval = setInterval(() => {
      this.changeFeaturedEntity();
    }, 7500);
  }

  private changeFeaturedEntity(): void {
    if (this.entities.length === 0) return;

    const randomIndex = Math.floor(Math.random() * this.entities.length);
    const entity = this.entities[randomIndex];
    this.featuredEntity = entity.Name || entity.DisplayName || 'Unknown Entity';
    this.featuredEntityObject = entity;

    this.updateUserState();
  }

  OpenFeaturedEntity(): void {
    if (!this.featuredEntityObject) return;

    this.OpenEntityRecord.emit({
      EntityName: 'MJ: Entities',
      RecordPKey: CompositeKey.FromID(this.featuredEntityObject.ID),
    });

    this.Interaction.emit({
      type: 'openEntity',
      entityName: this.featuredEntity,
      entityId: this.featuredEntityObject.ID
    });
  }

  SpeedUp(): void {
    this.speed = Math.min(this.speed + 0.25, 4);
    this.updateUserState();
  }

  SlowDown(): void {
    this.speed = Math.max(this.speed - 0.25, 0.25);
    this.updateUserState();
  }

  protected getSpeedDescription(): string {
    if (this.speed <= 0.5) return 'Very Slow';
    if (this.speed <= 1.0) return 'Slow';
    if (this.speed <= 1.5) return 'Normal';
    if (this.speed <= 2.5) return 'Fast';
    return 'Very Fast';
  }

  protected getLightColor(color: string, opacity: number = 0.1): string {
    const hex = color.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  TestStateSave(): void {
    const state = this.currentUserState;
    console.log('🧪 HelloDashboard: Manual test state save triggered:', state);
    console.log('📋 HelloDashboard: Current Config before manual save:', this.Config);

    this.UserStateChanged.emit(state);
    this.showStateUpdateNotification = true;

    setTimeout(() => {
      this.showStateUpdateNotification = false;
      console.log('🔍 HelloDashboard: Config after manual state save:', this.Config);
    }, 3000);
  }

  ChangeColor(): void {
    this.setRandomColor();

    this.Interaction.emit({
      type: 'colorChange',
      color: this.textColor,
      themeColor: this.themeColor
    });

    this.updateUserState();
  }

  private setRandomColor(): void {
    const currentColor = this.textColor;

    let newColor: string;
    do {
      const randomIndex = Math.floor(Math.random() * this.colors.length);
      newColor = this.colors[randomIndex];
    } while (newColor === currentColor && this.colors.length > 1);

    this.textColor = newColor;
    this.themeColor = newColor;
  }

  public override async Refresh(): Promise<void> {
    await super.Refresh();

    console.log('🔄 HelloDashboard: Refresh() called - reloading user state');

    this.stopAnimation();

    this.loadUserState();

    this.startAnimation();
  }
}

export function LoadHelloDashboard(): void {
  // Intentionally empty - existence prevents tree-shaking of the component
}
